import { Typography } from '@/components/UI/Typography'
import { useMenuStore } from '@/store/useMenuStore'
import { ActionIcon } from '@mantine/core'
import { IconBell, IconChevronLeft, IconMenu2, IconWorld, IconX } from '@tabler/icons-react'
import styles from './Header.module.css'
import { MenuDrawer } from './MenuDrawer'

export type HeaderProps = {
  /** ヘッダータイトル */
  title?: string
  /**
   * true の場合は左側に「戻る ＜」アイコンを表示する。
   * false（デフォルト）の場合は「ハンバーガー / X」を表示する。
   */
  showBack?: boolean
  /** 戻るボタンクリック（showBack=true のときのみ有効） */
  onBack?: () => void
  /**
   * true の場合は言語ボタンを右側に表示する。
   * キャンペーンに複数言語がある場合のみ true を渡す。
   */
  showLanguage?: boolean
  /** 言語ボタンクリック */
  onLanguage?: () => void
  /** ベルマークを出すかどうかのフラグ */
  showNotification?: boolean
  /** 通知バッジに表示する件数（0 または未指定でバッジ非表示） */
  notificationCount?: number
  /** 未読通知の赤い点バッジを表示するかどうか（件数なし）。notificationCount > 0 の場合は件数バッジが優先される */
  hasUnreadBadge?: boolean
  /** ベルボタンクリック */
  onNotification?: () => void
  className?: string
}

export function Header({
  title = 'タイトル',
  showBack = false,
  onBack,
  showLanguage = false,
  onLanguage,
  showNotification = true,
  notificationCount,
  hasUnreadBadge = false,
  onNotification,
  className,
}: HeaderProps) {
  const { isOpen, toggle } = useMenuStore()

  return (
    <>
      <header className={`${styles.root} ${className ?? ''}`}>
        {/* 左側：showBack=true なら戻るアイコン、false ならハンバーガー/X */}
        <div className={styles.leftGroup}>
          {showBack ? (
            <ActionIcon variant="subtle" color="dark" onClick={onBack} aria-label="戻る">
              <IconChevronLeft size={24} stroke={1.5} aria-hidden="true" />
            </ActionIcon>
          ) : (
            <ActionIcon
              variant="subtle"
              color="dark"
              onClick={toggle}
              aria-label={isOpen ? 'メニューを閉じる' : 'メニューを開く'}
            >
              {isOpen ? (
                <IconX size={24} stroke={1.5} aria-hidden="true" />
              ) : (
                <IconMenu2 size={24} stroke={1.5} aria-hidden="true" />
              )}
            </ActionIcon>
          )}
        </div>

        <Typography variant="subtitle1" component="p" className={styles.title}>
          {title}
        </Typography>

        {/* 右側：言語ボタン（条件付き）＋ ベルボタン */}
        <div className={styles.rightGroup}>
          {showLanguage && (
            <ActionIcon variant="subtle" color="dark" onClick={onLanguage} aria-label="言語設定">
              <IconWorld size={24} stroke={1.5} aria-hidden="true" />
            </ActionIcon>
          )}
          <div className={styles.notificationWrapper}>
            {showNotification && (
              <>
                <ActionIcon variant="subtle" color="dark" onClick={onNotification} aria-label="通知">
                  <IconBell size={24} stroke={1.5} aria-hidden="true" />
                </ActionIcon>
                {notificationCount != null && notificationCount > 0 && (
                  <div className={styles.badge}>
                    <span className={styles.badgeText}>{notificationCount}</span>
                  </div>
                )}
                {hasUnreadBadge && !(notificationCount != null && notificationCount > 0) && (
                  <div className={styles.dotBadge} />
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* ハンバーガー押下で表示されるドロワーメニュー（showBack=false 時のみ） */}
      {!showBack && <MenuDrawer />}
    </>
  )
}
